package dev.wyns.ventrova.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.data.model.FryOilStatus
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.data.model.fryOilStatus
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.fry_oil_report
import dev.wyns.ventrova.ui.theme.LocalVentrovaFonts
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.dayName
import dev.wyns.ventrova.ui.theme.description
import dev.wyns.ventrova.ui.theme.distanceLabel
import dev.wyns.ventrova.ui.theme.hoursOnDay
import dev.wyns.ventrova.ui.theme.icon
import dev.wyns.ventrova.ui.theme.ink
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.openStatusText
import dev.wyns.ventrova.ui.theme.palette
import dev.wyns.ventrova.ui.theme.shortLabel
import dev.wyns.ventrova.ui.theme.soft
import dev.wyns.ventrova.ui.theme.summary
import kotlinx.datetime.DayOfWeek
import org.jetbrains.compose.resources.stringResource

/** Body text style in the platform's body font. */
@Composable
fun bodyStyle(size: TextUnit, weight: FontWeight = FontWeight.Normal, color: Color = VentrovaColors.Ink) = TextStyle(
    fontFamily = LocalVentrovaFonts.current.body,
    fontSize = size,
    fontWeight = weight,
    color = color,
)

/** Cuisine tags as a readable label, e.g. "Pizza · Italian". */
fun Place.cuisineLabel(): String? =
    cuisines.filterNot { it in IgnoredCuisines || (category == PlaceCategory.ChipShop && it == "friture") }
        .take(3)
        .joinToString(" · ") { tag -> tag.replace('_', ' ').replaceFirstChar { it.uppercase() } }
        .ifBlank { null }

private val IgnoredCuisines = setOf("vegan", "vegetarian", "regional")

/** "● Veggie · Restaurant · 450 m" */
@Composable
fun LevelTypeDistanceLine(
    item: PlaceItem,
    modifier: Modifier = Modifier,
    style: TextStyle = bodyStyle(13.sp, color = VentrovaColors.Secondary),
    glyphSize: Dp = 10.dp,
    showCategory: Boolean = true,
    showCuisineInsteadOfCategory: Boolean = false,
) {
    val parts = buildList {
        add(stringResource(item.place.veganLevel.shortLabel))
        if (showCuisineInsteadOfCategory) {
            item.place.cuisineLabel()?.let(::add) ?: add(stringResource(item.place.category.label))
        } else if (showCategory) {
            add(stringResource(item.place.category.label))
        }
        item.distanceMeters?.let { add(distanceLabel(it)) }
    }
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(5.dp)) {
        LevelGlyph(item.place.veganLevel, size = glyphSize)
        Text(parts.joinToString(" · "), style = style, maxLines = 1, overflow = TextOverflow.Ellipsis)
    }
}

/** "Open · closes at 22:00", colored by status; nothing when the hours are unknown. */
@Composable
fun OpenStatusLine(
    item: PlaceItem,
    modifier: Modifier = Modifier,
    style: TextStyle = bodyStyle(13.sp, color = VentrovaColors.Secondary),
    prefix: String? = null,
    suffix: String? = null,
    showChange: Boolean = true,
) {
    val status = openStatusText(item.openStatus)
    if (status == null && prefix == null && suffix == null) return
    Text(
        buildAnnotatedString {
            prefix?.let { append(it) }
            if (status != null) {
                if (prefix != null) append(" · ")
                withStyle(SpanStyle(color = status.color, fontWeight = FontWeight.SemiBold)) { append(status.status) }
                if (showChange) status.change?.let { append(" · $it") }
            }
            suffix?.let { if (status != null || prefix != null) append(" · "); append(it) }
        },
        modifier = modifier,
        style = style,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
}

/** Pill with the level glyph and label in the level's colors. */
@Composable
fun LevelBadge(
    level: VeganLevel,
    modifier: Modifier = Modifier,
    shape: RoundedCornerShape = RoundedCornerShape(50),
    fontSize: TextUnit = 12.5.sp,
    glyphSize: Dp = 10.dp,
    contentPadding: PaddingValues = PaddingValues(start = 6.dp, end = 9.dp, top = 3.dp, bottom = 3.dp),
) {
    val palette = level.palette
    Row(
        modifier.background(palette.soft, shape).padding(contentPadding),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        LevelGlyph(level, size = glyphSize)
        Text(stringResource(level.label), style = bodyStyle(fontSize, FontWeight.SemiBold, palette.ink))
    }
}

/** Sizes of the wide blocks under the name on the detail page, per design. */
data class BannerStyle(
    val cornerRadius: Dp,
    val padding: PaddingValues,
    val titleSize: TextUnit,
    val bodySize: TextUnit,
    val glyphSize: Dp,
) {
    companion object {
        val Material = BannerStyle(16.dp, PaddingValues(start = 14.dp, end = 16.dp, top = 12.dp, bottom = 12.dp), 15.sp, 14.sp, 16.dp)
        val Cupertino = BannerStyle(16.dp, PaddingValues(start = 12.dp, end = 14.dp, top = 10.dp, bottom = 10.dp), 14.5.sp, 13.5.sp, 16.dp)
        val Web = BannerStyle(12.dp, PaddingValues(start = 10.dp, end = 12.dp, top = 9.dp, bottom = 9.dp), 14.sp, 13.sp, 15.dp)
    }
}

/** Wide level banner on the detail page: glyph, then the label with the description below it. */
@Composable
fun LevelBanner(level: VeganLevel, modifier: Modifier = Modifier, style: BannerStyle = BannerStyle.Cupertino) {
    val palette = level.palette
    InfoBanner(
        title = stringResource(level.label),
        description = stringResource(level.description),
        background = palette.soft,
        ink = palette.ink,
        style = style,
        modifier = modifier,
        descriptionAlpha = 0.85f,
        leading = { LevelGlyph(level, Modifier.padding(top = 2.dp), size = style.glyphSize, borderWidth = 2.5.dp) },
    )
}

/**
 * What a chip shop fries in, shown under the level banner on the detail page; nothing for other
 * places. When the oil is unknown it ends with a link to [onReport].
 */
@Composable
fun FryOilBanner(
    place: Place,
    onReport: () -> Unit,
    modifier: Modifier = Modifier,
    style: BannerStyle = BannerStyle.Cupertino,
    linkModifier: Modifier = Modifier,
) {
    val status = place.fryOilStatus ?: return
    InfoBanner(
        title = stringResource(status.label),
        description = stringResource(status.description),
        background = status.soft,
        ink = status.ink,
        style = style,
        modifier = modifier,
        descriptionAlpha = 0.9f,
        leading = { VIcon(status.icon, Modifier.padding(top = 1.dp), size = 18.dp, tint = status.ink) },
    ) {
        if (status == FryOilStatus.Unknown) {
            Text(
                stringResource(Res.string.fry_oil_report),
                style = bodyStyle(style.bodySize, FontWeight.SemiBold, VentrovaColors.Link),
                modifier = Modifier.padding(top = 6.dp).then(linkModifier).clickable(onClick = onReport),
            )
        }
    }
}

@Composable
private fun InfoBanner(
    title: String,
    description: String,
    background: Color,
    ink: Color,
    style: BannerStyle,
    modifier: Modifier,
    descriptionAlpha: Float,
    leading: @Composable () -> Unit,
    footer: @Composable () -> Unit = {},
) {
    Row(
        modifier.background(background, RoundedCornerShape(style.cornerRadius)).padding(style.padding),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        leading()
        Column(Modifier.weight(1f)) {
            Text(title, style = bodyStyle(style.titleSize, FontWeight.Bold, ink), maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(
                description,
                style = bodyStyle(style.bodySize, color = ink.copy(alpha = descriptionAlpha)).copy(lineHeight = style.bodySize * 1.35f),
                modifier = Modifier.padding(top = 1.dp),
            )
            footer()
        }
    }
}

/** "💧 Plant oil": the fry oil of a chip shop in list rows and preview cards; nothing for other places. */
@Composable
fun FryOilLine(place: Place, modifier: Modifier = Modifier, fontSize: TextUnit = 12.5.sp, iconSize: Dp = 14.5.dp) {
    val status = place.fryOilStatus ?: return
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
        VIcon(status.icon, size = iconSize, tint = status.ink)
        Text(stringResource(status.shortLabel), style = bodyStyle(fontSize, FontWeight.SemiBold, status.ink), maxLines = 1)
    }
}

/** The three levels with their meaning, as on the onboarding screens. */
@Composable
fun LevelLegend(modifier: Modifier = Modifier, glyphSize: Dp = 18.dp, fontSize: TextUnit = 15.5.sp, spacing: Dp = 12.dp) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(spacing)) {
        VeganLevel.entries.forEach { level ->
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                LevelGlyph(level, size = glyphSize, borderWidth = 3.dp)
                Text(
                    buildAnnotatedString {
                        withStyle(SpanStyle(fontWeight = FontWeight.SemiBold)) { append(stringResource(level.label)) }
                        withStyle(SpanStyle(color = VentrovaColors.Secondary)) { append("  " + stringResource(level.summary)) }
                    },
                    style = bodyStyle(fontSize),
                )
            }
        }
    }
}

/** Weekly opening hours, today in semibold. */
@Composable
fun WeekHours(item: PlaceItem, today: DayOfWeek, modifier: Modifier = Modifier, fontSize: TextUnit = 14.5.sp, rowPadding: Dp = 6.dp) {
    val hours = item.place.openingHours ?: return
    Column(modifier) {
        DayOfWeek.entries.forEach { day ->
            val weight = if (day == today) FontWeight.SemiBold else FontWeight.Normal
            Row(Modifier.padding(vertical = rowPadding)) {
                Text(dayName(day).replaceFirstChar { it.uppercase() }, style = bodyStyle(fontSize, weight), modifier = Modifier.weight(1f))
                Text(hoursOnDay(hours, day), style = bodyStyle(fontSize, weight))
            }
        }
    }
}
